import { BarChart2, ChevronDown, ChevronUp, Search, XCircle } from 'react-feather';
import { ChangeEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import omit from 'lodash/omit';
import styled, { css } from 'styled-components';

import { GetContactFiltersQuery as GetContactFilters, Locale } from '__generated__/graphql';

import { ParticipantFieldFilters } from '../ParticipantFieldFiltersForm';
import { encodeUrl, isEmpty, toggleInList } from '../../../common/helpers';
import EventPicker, { Event as SelectedEvent } from '../../Events/EventPicker';
import GetContactFiltersQuery from './GetContactFiltersQuery';
import UI from '../../../common/UI';
import config from '../../config';
import useLocale from '../../../common/useLocale';
import useProject from '../../useProject';
import useQuery from '../../../api/useQuery';

export type ParticipantStats = GetContactFilters['project']['participant_stats'];

export interface ParticipantFieldFilter {
  id: string;
  filters?: ParticipantFieldFilters;
}

export interface ContactFilterParams {
  event?: string | null;
  search?: string | null;
  locales?: Locale[];
  mail_opt_ins?: string[];
  received_messages?: string[];
  will_receive_messages?: string[];
}

export const defaultFilters: ContactFilterParams = {
  event: null,
  search: null,
  locales: [],
  mail_opt_ins: [],
  received_messages: [],
  will_receive_messages: [],
};

export interface ContactFiltersProps {
  types?: (keyof ContactFilterParams)[];
  filters: ContactFilterParams;
  onChange: (update: (filters: ContactFilterParams) => ContactFilterParams) => void;
  onStatsChange?: (stats: ParticipantStats) => void;
  showTotals?: boolean;
  showResetButton?: boolean;
  showViewParticipantsButton?: boolean;
  readOnly?: boolean;
}

const ContactFilters = ({
  types, filters: incomingFilters, onChange, onStatsChange, showTotals = true, showResetButton = true,
  showViewParticipantsButton = false, readOnly = false,
}: ContactFiltersProps) => {
  const { t } = useTranslation();
  const project = useProject();
  const { formatNumber } = useLocale();

  const enabled = (type: keyof ContactFilterParams) => !types || types.includes(type);

  const filters = {
    ...defaultFilters,
    ...incomingFilters,
  };

  const searchRef = useRef<HTMLInputElement>();

  // Fetches the filters
  const { data, loading } = useQuery(
    GetContactFiltersQuery,
    {
      variables: {
        project_id: project.id,
        ...omit(filters, 'event'),
        events: filters.event ? [filters.event] : [],
      },
      fetchPolicy: 'cache-and-network',
      onCompleted: (data) => {
        onStatsChange?.(data.project.participant_stats);
      },
    },
  );

  const mailPermissions = data?.project.mail_permissions || [];
  const messages = data?.project.messages.data || [];
  const stats = data?.project.participant_stats;

  const getDefaultShowingFilters = (filters: ContactFilterParams) => ({
    locales: filters.locales?.length > 0,
    mail_opt_ins: filters.mail_opt_ins?.length > 0,
    received_messages: filters.received_messages?.length > 0,
    will_receive_messages: filters.will_receive_messages?.length > 0,
  });

  // Indicates whether a filter category is collapsed or not
  const [showingFilters, setShowingFilters] = useState(() => getDefaultShowingFilters(filters));

  /**
   * Shows/hides a filter category.
   */
  const toggleShowingFilters = (key: keyof typeof showingFilters) => {
    setShowingFilters((showing) => ({
      ...showing,
      [key]: !showing[key],
    }));
  };

  const selectEvent = (event?: SelectedEvent) => {
    onChange((filters) => ({
      ...defaultFilters, // Reset all filters except search.
      search: filters.search,
      event: event?.id,
    }));
  };

  /**
   * Handles changes to the search input by storing the value in the filters.
   */
  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    onChange((filters) => ({ ...defaultFilters, ...filters, search: event.target.value || null }));
  };

  /**
   * Unsets the search term in the filters and empties the input.
   */
  const resetSearch = () => {
    onChange((filters) => ({ ...defaultFilters, ...filters, search: null }));

    searchRef.current.value = '';
  };

  /**
   * Toggles a filter in a list: if it's not in the list, it's added, otherwise it's removed.
   */
  const toggleListFilter = (
    name: 'locales' | 'mail_opt_ins' | 'received_messages' | 'will_receive_messages',
    id: string,
  ) => {
    onChange((filters) => ({
      ...defaultFilters,
      ...filters,
      [name]: toggleInList([...filters[name] || []], id),
    }));
  };

  /**
   * Indicates whether a user has activated any filters.
   */
  const hasFilters = Object.values(filters).some((param) => !isEmpty(param));

  /**
   * Resets all filters, the UI state and search input
   */
  const resetFilters = (filters?: ContactFilterParams) => {
    const newFilters = { ...defaultFilters, ...filters };

    onChange(() => newFilters);
    setShowingFilters(getDefaultShowingFilters(newFilters));

    if (searchRef.current) {
      searchRef.current.value = newFilters.search || '';
    }
  };

  return (
    <UI.FormGrid sc={{ gutter: 0.75 }}>
      {enabled('event') && (
        <UI.Div>
          <EventPicker
            eventId={filters.event}
            onChange={selectEvent}
            renderActive={(event) => (
              <UI.Span sc={{ color: 'gray.800' }} style={{ fontWeight: '500' }}>
                {event?.title || t('all_events')}
              </UI.Span>
            )}
          />
        </UI.Div>
      )}

      {enabled('search') && (
        <UI.InputGroup>
          <UI.InputWrapper>
            <UI.Span>
              <UI.Icon>
                <Search />
              </UI.Icon>
            </UI.Span>
            <UI.DebouncedInput
              type="text"
              value={filters.search}
              onChange={handleSearch}
              placeholder={t('search_contacts')}
              ref={searchRef}
              sc={{ px: 5 }}
              disabled={readOnly}
            />
            {filters.search && (
              <UI.Span>
                <UI.A onClick={() => resetSearch()} sc={{ color: 'gray.400' }}>
                  <UI.Icon>
                    <XCircle />
                  </UI.Icon>
                </UI.A>
              </UI.Span>
            )}
          </UI.InputWrapper>
        </UI.InputGroup>
      )}

      {enabled('locales') && (
        <>
          <UI.HR style={{ borderStyle: 'dotted' }} />
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('locales')}
              role="button"
              sc={{ mb: showingFilters.locales ? 2 : 0 }}
            >
              {t('preferred_language')}
              {' '}
              {!showingFilters.locales && filters.locales.length > 0 && (
              <UI.Badge>
                {filters.locales.length}
              </UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.locales && <ChevronUp />}
                {!showingFilters.locales && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.locales && config.locales.map((locale) => (
              <UI.Checkbox
                key={`locale_${locale}`}
                checked={filters.locales.includes(locale)}
                onChange={() => toggleListFilter('locales', locale)}
                disabled={readOnly}
              >
                {t(`common:locales.${locale}`)}
              </UI.Checkbox>
            ))}
          </UI.InputGroup>
        </>
      )}

      {filters.received_messages?.length > 0 && (
        <>
          <UI.HR style={{ borderStyle: 'dotted' }} />
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('received_messages')}
              role="button"
              sc={{ mb: showingFilters.received_messages ? 2 : 0 }}
            >
              {t('received_message')}
              {' '}
              {!showingFilters.received_messages && filters.received_messages.length > 0 && (
                <UI.Badge>{filters.received_messages.length}</UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.received_messages && <ChevronUp />}
                {!showingFilters.received_messages && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.received_messages && messages.map((message) => (
              filters.received_messages.includes(message.id) && (
                <UI.Div key={message.id}>
                  <UI.Checkbox
                    checked={filters.received_messages.includes(message.id)}
                    onChange={() => toggleListFilter('received_messages', message.id)}
                    disabled={readOnly}
                  >
                    {message.title}
                  </UI.Checkbox>
                </UI.Div>
              )
            ))}
          </UI.InputGroup>
        </>
      )}

      {filters.will_receive_messages?.length > 0 && (
        <>
          <UI.HR style={{ borderStyle: 'dotted' }} />
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('will_receive_messages')}
              role="button"
              sc={{ mb: showingFilters.will_receive_messages ? 2 : 0 }}
            >
              {t('will_receive_message')}
              {' '}
              {!showingFilters.will_receive_messages && filters.will_receive_messages.length > 0 && (
                <UI.Badge>{filters.will_receive_messages.length}</UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.will_receive_messages && <ChevronUp />}
                {!showingFilters.will_receive_messages && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.will_receive_messages && messages.map((message) => (
              filters.will_receive_messages.includes(message.id) && (
                <UI.Div key={message.id}>
                  <UI.Checkbox
                    checked={filters.will_receive_messages.includes(message.id)}
                    onChange={() => toggleListFilter('will_receive_messages', message.id)}
                    disabled={readOnly}
                  >
                    {message.title}
                  </UI.Checkbox>
                </UI.Div>
              )
            ))}
          </UI.InputGroup>
        </>
      )}

      {mailPermissions.length > 0 && enabled('mail_opt_ins') && (
        <>
          <UI.HR style={{ borderStyle: 'dotted' }} />
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('mail_opt_ins')}
              role="button"
              sc={{ mb: showingFilters.mail_opt_ins ? 2 : 0 }}
            >
              {t('mail_opt_ins')}
              {' '}
              {!showingFilters.mail_opt_ins && filters.mail_opt_ins?.length > 0 && (
                <UI.Badge>{filters.mail_opt_ins.length}</UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.mail_opt_ins && <ChevronUp />}
                {!showingFilters.mail_opt_ins && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.mail_opt_ins && mailPermissions.map((mailPermission) => (
              <UI.Checkbox
                onChange={() => toggleListFilter('mail_opt_ins', mailPermission.id)}
                checked={filters.mail_opt_ins.includes(mailPermission.id)}
                disabled={readOnly}
                key={mailPermission.id}
              >
                {mailPermission.title}
              </UI.Checkbox>
            ))}
          </UI.InputGroup>
        </>
      )}

      {showTotals && (
        <>
          <UI.HR style={{ borderStyle: 'dotted' }} />

          <UI.Div sc={{ loading }}>
            <UI.Div sc={{ mb: 2 }}>
              <UI.Delimit>
                <UI.Span sc={{ color: 'gray.600', inline: true }} style={{ fontWeight: 500 }}>
                  <UI.Icon sc={{ mr: 1 }}>
                    <BarChart2 />
                  </UI.Icon>
                  {t('in_this_selection')}
                </UI.Span>
                {showViewParticipantsButton && (
                <UI.A href={`/b/${project.id}/participants?${encodeUrl(filters)}`} target="_blank">
                  {t('view')}
                </UI.A>
                )}
              </UI.Delimit>
            </UI.Div>
            <UI.FlexContainer>
              <UI.Div sc={{ mr: 3 }} style={{ minWidth: 55 }}>
                <UI.H4>{formatNumber(stats?.participants || 0)}</UI.H4>
                <UI.Small>{t('contacts', { count: stats?.participants || 0 })}</UI.Small>
              </UI.Div>
            </UI.FlexContainer>
          </UI.Div>
        </>
      )}

      {showResetButton && hasFilters && (
        <>
          <UI.HR style={{ borderStyle: 'dotted' }} />
          <UI.Div>
            <UI.Button onClick={() => resetFilters()}>
              {t('reset_filters')}
            </UI.Button>
          </UI.Div>
        </>
      )}
    </UI.FormGrid>
  );
};

const CategoryHeading = styled(UI.A)`
  ${({ theme }) => css`
    color: ${theme.colors.gray[600]};
    font-weight: 500;
    display: block;
    line-height: 21px;
  `}
`;

export default ContactFilters;
